import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { blendColors } from '../utils/tagColors';

const BubbleContainer = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  border-radius: 16px;
  border: none;
  cursor: pointer;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 4px 15px ${props => props.theme.colors.shadow},
    0 0 10px ${props => props.theme.colors.borderLight};
  transition: box-shadow 0.2s ease;
  overflow: hidden;
  
  display: flex;
  flex-direction: column;
  
  min-height: 80px;
  width: 100%;
  
  &:hover {
    box-shadow: 
      0 8px 25px ${props => props.theme.colors.shadow},
      0 0 20px rgba(100, 255, 218, 0.4),
      0 0 40px rgba(100, 255, 218, 0.2);
  }
`;

const ColorBanner = styled.div`
  background: ${props => props.tagBackground || props.theme.colors.surfaceSecondary};
  height: 24px;
  width: 100%;
  flex-shrink: 0;
  
  /* Add subtle hover effect */
  transition: all 0.3s ease;
  
  ${BubbleContainer}:hover & {
    background: ${props => props.theme.colors.hover};
  }
`;

const BubbleContent = styled.div`
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
`;

const BubbleText = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.3;
  word-wrap: break-word;
  hyphens: auto;
  
  ${BubbleContainer}:hover & {
    color: ${props => props.theme.colors.text};
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const PredictionBubble = ({ prediction, onClick, delay = 0 }) => {
  const { themeData } = useTheme();
  const bubbleVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 20
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        delay: delay,
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  // Generate blended colors for this prediction
  const tagBackground = blendColors(prediction.tags, themeData.colors.bubbleAlpha);

  return (
    <BubbleContainer
      variants={bubbleVariants}
      initial="hidden"
      animate="visible"
      onClick={onClick}
      theme={themeData}
    >
      <ColorBanner tagBackground={tagBackground} theme={themeData} />
      <BubbleContent>
        <BubbleText theme={themeData}>
          {prediction.title || `Prediction ${prediction.id}`}
        </BubbleText>
      </BubbleContent>
    </BubbleContainer>
  );
};

PredictionBubble.propTypes = {
  prediction: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    tags: PropTypes.string
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  delay: PropTypes.number
};

PredictionBubble.defaultProps = {
  delay: 0
};

export default PredictionBubble; 